import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, CircularProgress, FormControl, InputLabel,
  MenuItem, Select, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import api from '../../services/api';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'SUCESSO', label: 'Sucesso' },
  { value: 'ERRO', label: 'Erro' }
];

const TYPE_LABELS = {
  INSCRICAO: 'Inscrição',
  EMAIL: 'E-mail'
};

const formatDate = (value) => value
  ? new Date(value).toLocaleString('pt-BR')
  : '-';

export default function Logs() {
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [eventId, setEventId] = useState('');
  const [status, setStatus] = useState('');
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState('');

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await api.get('/events?filters[deleted][$eq]=false&fields[0]=name&pagination[page]=1&pagination[pageSize]=100&sort=id:desc');
      setEvents(data.data || []);
    } catch (err) {
      setError('Erro ao carregar eventos: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({
        'pagination[page]': '1',
        'pagination[pageSize]': '100'
      });
      if (eventId) params.set('filters[event][id][$eq]', eventId);
      if (status) params.set('filters[status][$eq]', status);

      const { data } = await api.get(`/communication-logs?${params.toString()}`);
      setLogs(data.data || []);
    } catch (err) {
      setError('Erro ao carregar logs: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setLoadingLogs(false);
    }
  }, [eventId, status]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const getAttributes = (item) => item.attributes || item;
  const getEventName = (item) => {
    const event = getAttributes(item).event;
    return event?.data?.attributes?.name || event?.name || '-';
  };

  return (
    <Card sx={{ margin: 'auto', mt: 5, mx: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">Logs de Inscrições e E-mails</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 280 }} size="small">
            <InputLabel id="logs-event-label">Evento</InputLabel>
            <Select
              labelId="logs-event-label"
              value={eventId}
              label="Evento"
              onChange={(e) => setEventId(e.target.value)}
              disabled={loadingEvents}
            >
              <MenuItem value="">Todos os eventos</MenuItem>
              {events.map((event) => {
                const attr = getAttributes(event);
                return <MenuItem key={event.id} value={event.id}>{attr.name}</MenuItem>;
              })}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="logs-status-label">Status</InputLabel>
            <Select
              labelId="logs-status-label"
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loadingLogs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Destinatário</TableCell>
                  <TableCell>Assunto</TableCell>
                  <TableCell>Mensagem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center">Nenhum log encontrado.</TableCell></TableRow>
                ) : logs.map((item) => {
                  const attr = getAttributes(item);
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(attr.createdAt || item.createdAt)}</TableCell>
                      <TableCell>{getEventName(item)}</TableCell>
                      <TableCell>{TYPE_LABELS[attr.type] || attr.type || '-'}</TableCell>
                      <TableCell>
                        <Typography color={attr.status === 'ERRO' ? 'error' : 'success.main'}>
                          {attr.status === 'ERRO' ? 'Erro' : 'Sucesso'}
                        </Typography>
                      </TableCell>
                      <TableCell>{attr.recipient || '-'}</TableCell>
                      <TableCell>{attr.subject || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>{attr.message || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Card>
  );
}
